import type {
  MessageGeneration,
  MessageSubagentRun,
  SubagentStreamStatus,
} from '../types/chat';
import { formatSkillDisplayName } from './parse-message-skill-events';

function parseOptionalString(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }
  const text = String(value).trim();
  return text || undefined;
}

function parseStreamDurationMs(data: Record<string, unknown>): number | undefined {
  const rawDuration = data.duration_ms ?? data.durationMs ?? data.DurationMs;
  if (typeof rawDuration === 'number' && Number.isFinite(rawDuration)) {
    return rawDuration;
  }
  if (typeof rawDuration === 'string') {
    const parsed = Number.parseFloat(rawDuration);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

function parseStreamSubagentRunBase(
  data: unknown
): Pick<MessageSubagentRun, 'runId' | 'subagentId' | 'subagentName'> | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const o = data as Record<string, unknown>;
  const subagentKey = String(o.subagent ?? o.Subagent ?? '').trim();
  const runId = String(
    o.run_id ??
      o.subagent_run_id ??
      o.subagentRunId ??
      o.runId ??
      o.RunId ??
      o.id ??
      o.Id ??
      ''
  ).trim();
  const subagentId = String(
    o.subagent_id ??
      o.subagentId ??
      o.SubagentId ??
      o.agent_id ??
      o.agentId ??
      subagentKey ??
      runId ??
      ''
  ).trim();
  const subagentName = String(
    o.subagent_name ??
      o.subagentName ??
      o.SubagentName ??
      o.agent_name ??
      o.agentName ??
      o.name ??
      o.Name ??
      subagentKey ??
      ''
  ).trim();

  if (!runId && !subagentId && !subagentName) {
    return null;
  }

  const resolvedRunId = runId || subagentId || subagentName;
  const resolvedSubagentId = subagentId || runId || subagentName;

  const rawName = subagentName || resolvedSubagentId;

  return {
    runId: resolvedRunId,
    subagentId: resolvedSubagentId,
    subagentName: formatSkillDisplayName(rawName) || rawName,
  };
}

export function normalizeSubagentStreamEventType(eventType: string): SubagentStreamStatus | null {
  const normalized = eventType.trim().toLowerCase().replace(/\./g, '_');

  if (normalized === 'subagent_started') {
    return 'started';
  }

  if (normalized === 'subagent_completed') {
    return 'completed';
  }

  return null;
}

/** WebSocket subagent.started payload. */
export function parseSubagentRunFromStreamStartedPayload(data: unknown): MessageSubagentRun | null {
  const base = parseStreamSubagentRunBase(data);
  if (!base) {
    return null;
  }

  return {
    ...base,
    status: 'running',
  };
}

/** WebSocket subagent.completed payload. */
export function parseSubagentRunFromStreamCompletedPayload(data: unknown): MessageSubagentRun | null {
  const base = parseStreamSubagentRunBase(data);
  if (!base) {
    return null;
  }

  const o = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const durationMs = parseStreamDurationMs(o);
  const error =
    o.error != null ? String(o.error) : o.Error != null ? String(o.Error) : null;
  const status =
    error != null
      ? 'error'
      : o.status != null
        ? String(o.status)
        : o.Status != null
          ? String(o.Status)
          : 'completed';

  return {
    ...base,
    ...(durationMs != null ? { durationMs } : {}),
    status,
    error,
  };
}

function parseSubagentRunRecord(item: unknown): MessageSubagentRun | null {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const base = parseStreamSubagentRunBase(item);
  if (!base) {
    return null;
  }

  const o = item as Record<string, unknown>;
  const durationMs = parseStreamDurationMs(o);
  const error =
    o.error != null ? String(o.error) : o.Error != null ? String(o.Error) : null;
  const status = parseOptionalString(o.status ?? o.Status) ?? 'completed';

  return {
    ...base,
    ...(durationMs != null ? { durationMs } : {}),
    status,
    error,
  };
}

/** Reads persisted subagent audit records from message metadata. */
export function parseSubagentRunsFromMetadata(metadata: unknown): MessageSubagentRun[] | undefined {
  if (!metadata || typeof metadata !== 'object') {
    return undefined;
  }

  const meta = metadata as Record<string, unknown>;
  const agentRun =
    meta.AgentRun && typeof meta.AgentRun === 'object'
      ? (meta.AgentRun as Record<string, unknown>)
      : meta.agentRun && typeof meta.agentRun === 'object'
        ? (meta.agentRun as Record<string, unknown>)
        : null;

  const rawRuns =
    agentRun?.SubagentRuns ??
    agentRun?.subagentRuns ??
    meta.subagentRuns ??
    meta.subagent_runs;

  if (!Array.isArray(rawRuns) || rawRuns.length === 0) {
    return undefined;
  }

  const parsed: MessageSubagentRun[] = [];
  for (const item of rawRuns) {
    const run = parseSubagentRunRecord(item);
    if (run) {
      parsed.push(run);
    }
  }

  return parsed.length > 0 ? parsed : undefined;
}

export function upsertMessageSubagentRun(
  runs: MessageSubagentRun[] | undefined,
  next: MessageSubagentRun
): MessageSubagentRun[] {
  const list = runs ? [...runs] : [];
  const index = list.findIndex((run) => run.runId === next.runId || run.subagentId === next.subagentId);
  if (index >= 0) {
    list[index] = { ...list[index], ...next };
    return list;
  }
  return [...list, next];
}

export function getSubagentStreamStatusLabel(
  status: SubagentStreamStatus,
  subagentName?: string
): string {
  const name = subagentName?.trim();
  if (status === 'started') {
    return name ? `Subagent started: ${name}` : 'Subagent started';
  }
  return name ? `Subagent finished: ${name}` : 'Subagent finished';
}

export function shouldRenderSubagentStreamStatus(
  generation?: Pick<MessageGeneration, 'content' | 'subagentStreamStatus'>
): boolean {
  if (!generation?.subagentStreamStatus) {
    return false;
  }
  return !generation?.content?.trim();
}

export function clearSubagentStreamStatusFields<T extends MessageGeneration>(generation: T): T {
  const { subagentStreamStatus: _s, activeSubagentName: _n, ...rest } = generation;
  return rest as T;
}

export interface GroupedMessageSubagentRun {
  runId: string;
  subagentId: string;
  subagentName: string;
  count: number;
  totalDurationMs?: number;
}

export function groupMessageSubagentRunsBySubagentId(
  runs: MessageSubagentRun[]
): GroupedMessageSubagentRun[] {
  const grouped = new Map<string, GroupedMessageSubagentRun>();

  for (const run of runs) {
    const key = run.subagentId || run.runId || run.subagentName;
    const existing = grouped.get(key);

    if (existing) {
      existing.count += 1;
      if (run.durationMs != null) {
        existing.totalDurationMs = (existing.totalDurationMs ?? 0) + run.durationMs;
      }
      continue;
    }

    grouped.set(key, {
      runId: run.runId || key,
      subagentId: key,
      subagentName: run.subagentName || key,
      count: 1,
      ...(run.durationMs != null && { totalDurationMs: run.durationMs }),
    });
  }

  return Array.from(grouped.values());
}
